// Import model
import { Form } from '../models/Form';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Field } from '../models/Field';
import { FormAction } from '../models/FormAction';
import { FormLayout } from '../models/FormLayout';
import { Menu } from '../models/Menu';

// Import dependency serializers (for complex types)
import { FieldSerializer } from './FieldSerializer';
import { FormActionSerializer } from './FormActionSerializer';
import { FormLayoutSerializer } from './FormLayoutSerializer';
import { MenuSerializer } from './MenuSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Form - handles JSON serialization and deserialization with type validation
 */
export class FormSerializer {
  /**
   * Deserializes a JSON string into a Form instance
   */
  static deserialize(json: string): Form {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Form instance
   */
  static fromObject(obj: any): Form {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Form');
    }


    const instance: Form = {
      name: obj['Name'] as string,
      title: obj['Title'] as string | undefined,
      isReadOnly: obj['IsReadOnly'] as boolean | undefined,
      fields: obj['Fields'] ? (obj['Fields'] as any[]).map((item: any) =>
        FieldSerializer.fromObject(item)
      ) : [] as any,
      menu: obj['Menu'] ? MenuSerializer.fromObject(obj['Menu']) : undefined as any,
      objectType: obj['ObjectType'] as string | undefined,
      objectTypeName: obj['ObjectType|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      layouts: obj['Layouts'] ? FormLayoutSerializer.fromObject(obj['Layouts']) : undefined as any,
      actions: obj['Actions'] ? (obj['Actions'] as any[]).map((item: any) =>
        FormActionSerializer.fromObject(item)
      ) : [] as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Form instances
   */
  static deserializeArray(json: string): Form[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Form instance to a JSON string
   */
  static serialize(instance: Form): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Form instance to a plain object
   */
  static toObject(instance: Form): any {
    return {
      'Name': instance.name,
      'Title': instance.title,
      'IsReadOnly': instance.isReadOnly,
      'Fields': instance.fields?.map((item: any) =>
        FieldSerializer.toObject(item)
      ),
      'Menu': instance.menu ? MenuSerializer.toObject(instance.menu) : undefined,
      'ObjectType': instance.objectType,
      'ObjectType|Name': instance.objectTypeName,
      '_id': instance.id,
      'Layouts': instance.layouts ? FormLayoutSerializer.toObject(instance.layouts) : undefined,
      'Actions': instance.actions?.map((item: any) =>
        FormActionSerializer.toObject(item)
      ),
    };
  }

  /**
   * Serializes an array of Form instances to a JSON string
   */
  static serializeArray(instances: Form[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
